import type { ZodTypeAny } from 'zod';

import { apiUrl } from './env';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isConflictError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

export function hasManualAssignmentsConflict(error: unknown): boolean {
  if (!isConflictError(error)) {
    return false;
  }
  const apiError = error as ApiError;
  return (
    apiError.message.includes('ручные назначения') &&
    apiError.message.includes('удалит все назначения')
  );
}

/** Ответ пришёл, но не соответствует ожидаемому контракту. */
export class ApiContractError extends Error {
  readonly issues: string;

  constructor(path: string, issues: string) {
    super(`Ответ ${path} не соответствует ожидаемому формату`);
    this.name = 'ApiContractError';
    this.issues = issues;
  }
}

export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function describeError(error: unknown): string {
  if (error instanceof ApiContractError) {
    return `${error.message}: ${error.issues}`;
  }
  if (error instanceof ApiError) {
    return `HTTP ${error.status}: ${error.message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * Разбирает конверт ошибки apps/api: `{ error: { code, message, requestId, ... } }`.
 * Дополнительно поддерживаются простые формы `{ message }` и `{ detail }`.
 */
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (text === '') {
      return response.statusText || 'Ошибка запроса';
    }
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed !== null && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const envelope = record['error'];
        if (envelope !== null && typeof envelope === 'object') {
          const message = (envelope as Record<string, unknown>)['message'];
          if (typeof message === 'string' && message !== '') {
            return message;
          }
        }
        for (const key of ['message', 'detail', 'error'] as const) {
          const value = record[key];
          if (typeof value === 'string' && value !== '') {
            return value;
          }
        }
      }
    } catch {
      // не JSON — вернём как есть
    }
    return text.slice(0, 300);
  } catch {
    return response.statusText || 'Ошибка запроса';
  }
}

export async function fetchJson<TSchema extends ZodTypeAny>(
  path: string,
  schema: TSchema,
  init?: RequestInit,
): Promise<TSchema['_output']> {
  const url = apiUrl(path);
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');

  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    // Автоматический редирект на /login при 401 (требуется аутентификация)
    if (response.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
      // Возвращаем отложенный промис, чтобы избежать дублирования ошибок в UI
      return new Promise(() => {});
    }
    throw new ApiError(await readErrorMessage(response), response.status);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiContractError(path, 'сервис вернул не JSON');
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    const issues = result.error.issues
      .slice(0, 3)
      .map((issue) => `${issue.path.join('.') || '<root>'} — ${issue.message}`)
      .join('; ');
    throw new ApiContractError(path, issues);
  }

  return result.data;
}
